import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  FileCheck,
  Download,
  Search,
  QrCode,
  CheckCircle2,
  Clock,
  Trash2,
  Printer,
} from "lucide-react";
import { toast } from "sonner";
import QRCode from "qrcode";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SiteInductionRecords() {
  const { user } = useContext(AuthContext);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [inductions, setInductions] = useState([]);
  const [filteredInductions, setFilteredInductions] = useState([]);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [selectedSiteName, setSelectedSiteName] = useState("");
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedInduction, setSelectedInduction] = useState(null);
  const [showAddInductionDialog, setShowAddInductionDialog] = useState(false);
  const [addInductionSite, setAddInductionSite] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {id, name}

  const token = localStorage.getItem("token");

  // Load sites
  useEffect(() => {
    const loadSites = async () => {
      try {
        const response = await fetch(`${API}/sites`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setSites(data);
        }
      } catch (error) {
        console.error("Error loading sites:", error);
      }
    };

    loadSites();
  }, [token]);

  // Load inductions
  useEffect(() => {
    loadInductions();
  }, [selectedSite, token]);

  const loadInductions = async () => {
    setLoading(true);
    try {
      const url = selectedSite === "all" 
        ? `${API}/inductions`
        : `${API}/inductions?site_id=${selectedSite}`;
        
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setInductions(data);
      }
    } catch (error) {
      console.error("Error loading inductions:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter inductions by search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredInductions(inductions);
    } else {
      const filtered = inductions.filter(ind => 
        ind.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ind.surname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ind.contact_number.includes(searchTerm) ||
        ind.company_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredInductions(filtered);
    }
  }, [searchTerm, inductions]);

  const generateQRCode = async (siteId) => {
    const site = sites.find(s => s.id === siteId);
    if (!site) return;

    if (!site.induction_qr_code_url) {
      toast.error("This site does not have an induction QR code URL configured");
      return;
    }

    const siteName = site.name;
    const inductionUrl = `${window.location.origin}${site.induction_qr_code_url}`;
    
    console.log("Generated QR code URL:", inductionUrl);
    
    try {
      // Generate QR code
      const qrDataUrl = await QRCode.toDataURL(inductionUrl, {
        width: 400,
        margin: 2,
      });

      // Create canvas to add text labels
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas size (QR code + space for text)
      const qrSize = 400;
      const padding = 40;
      const titleHeight = 60;
      const siteNameHeight = 50;
      const bottomPadding = 20;
      canvas.width = qrSize + (padding * 2);
      canvas.height = qrSize + titleHeight + siteNameHeight + bottomPadding + (padding * 2);
      
      // Fill white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw title
      ctx.fillStyle = '#1e293b';
      ctx.font = 'bold 32px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Site Induction QR Code', canvas.width / 2, padding + 35);
      
      // Draw site name
      ctx.fillStyle = '#2563eb';
      ctx.font = 'bold 24px Arial, sans-serif';
      ctx.fillText(siteName, canvas.width / 2, padding + titleHeight + 30);
      
      // Draw QR code image
      const qrImage = new Image();
      qrImage.onload = () => {
        ctx.drawImage(qrImage, padding, padding + titleHeight + siteNameHeight, qrSize, qrSize);
        
        // Convert canvas to data URL
        const finalDataUrl = canvas.toDataURL('image/png');
        setQrCodeUrl(finalDataUrl);
        setSelectedSiteName(siteName);
        setShowQRDialog(true);
      };
      qrImage.src = qrDataUrl;
      
    } catch (error) {
      console.error("Error generating QR code:", error);
      toast.error("Failed to generate QR code");
    }
  };

  const downloadQRCode = () => {
    const a = document.createElement("a");
    a.href = qrCodeUrl;
    a.download = `Site_Induction_QR_${selectedSiteName.replace(/\s/g, "_")}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("QR code downloaded");
  };

  const handleDeleteInduction = async (inductionId, personName) => {
    try {
      const response = await fetch(`${API}/ohs/inductions/${inductionId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success(`Deleted induction for ${personName}`);
        // Reload inductions
        loadInductions();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete induction");
      }
    } catch (error) {
      console.error("Error deleting induction:", error);
      toast.error("Failed to delete induction");
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handlePrintInduction = () => {
    window.print();
  };

  const handleExportCSV = () => {
    if (filteredInductions.length === 0) {
      toast.error("No induction records to export");
      return;
    }

    // Define CSV headers
    const headers = [
      "Induction Number",
      "Date",
      "First Name",
      "Surname",
      "Company",
      "Occupation",
      "Employment Status",
      "Date of Birth",
      "Contact Number",
      "Emergency Contact Name",
      "Emergency Relationship",
      "Emergency Phone",
      "Site Name",
      "Site Manager",
      "Status",
      "Pre-existing Injury",
      "Medication/Allergy",
      "ID Type",
      "Industry Induction Card",
      "High Risk License",
      "Other Competencies",
      "Induction Explained",
      "Understand Induction",
      "Agree to Comply",
      "Opportunity for Questions",
      "Employer Communicated SWMS",
    ];

    // Convert induction records to CSV rows
    const rows = filteredInductions.map((induction) => {
      const site = sites.find(s => s.id === induction.site_id);
      const isCompleted = Boolean(induction.inductor_signature);
      
      return [
        induction.induction_number || "",
        new Date(induction.created_at).toLocaleDateString("en-AU"),
        induction.first_name || "",
        induction.surname || "",
        induction.company_name || "",
        induction.occupation || "",
        induction.employment_status || "",
        induction.date_of_birth || "",
        induction.contact_number || "",
        induction.emergency_contact_name || "",
        induction.emergency_relationship || "",
        induction.emergency_phone || "",
        site?.name || "",
        induction.inductor_name || "Pending",
        isCompleted ? "Completed" : "Pending",
        induction.pre_existing_injury ? "Yes" : "No",
        induction.medication_allergy ? "Yes" : "No",
        induction.identification_type || "",
        induction.industry_induction_card_number || "",
        induction.high_risk_license_type || "",
        induction.other_competencies || "",
        induction.induction_explained ? "Yes" : "No",
        induction.understand_induction ? "Yes" : "No",
        induction.agree_to_comply ? "Yes" : "No",
        induction.opportunity_for_questions ? "Yes" : "No",
        induction.employer_communicated_swms ? "Yes" : "No",
      ];
    });

    // Create CSV content
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    link.setAttribute("href", url);
    link.setAttribute("download", `Site_Induction_Records_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exported ${filteredInductions.length} induction records to CSV`);
  };

  // Calculate statistics
  const totalInductions = filteredInductions.length;
  const completedInductions = filteredInductions.filter(ind => ind.inductor_signature).length;
  const pendingInductions = totalInductions - completedInductions;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">Site Induction Records</h1>
        <p className="text-sm sm:text-base text-slate-600">Manage and track site safety induction compliance</p>
      </div>

      {/* Controls */}
      <Card className="mb-4 sm:mb-6">
        <CardContent className="pt-4 sm:pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <Label htmlFor="site-filter">Filter by Site</Label>
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger id="site-filter">
                  <SelectValue placeholder="All Sites" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sites</SelectItem>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="search">Search Personnel</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="search"
                  type="text"
                  placeholder="Name or mobile..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="qr-site">QR Code</Label>
              <Select onValueChange={(value) => generateQRCode(value)}>
                <SelectTrigger id="qr-site">
                  <SelectValue placeholder="Select site for QR" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end gap-2">
              <Button 
                className="flex-1"
                onClick={() => setShowAddInductionDialog(true)}
                variant="outline"
              >
                <FileCheck className="w-4 h-4 mr-2" />
                Add Induction
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleExportCSV}
                disabled={filteredInductions.length === 0}
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <Card>
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-600">Total Inductions</p>
                <p className="text-2xl sm:text-3xl font-bold text-slate-800">{totalInductions}</p>
              </div>
              <FileCheck className="w-8 h-8 sm:w-12 sm:h-12 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-600">Completed</p>
                <p className="text-2xl sm:text-3xl font-bold text-green-600">{completedInductions}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 sm:w-12 sm:h-12 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-600">Pending Signature</p>
                <p className="text-2xl sm:text-3xl font-bold text-orange-600">{pendingInductions}</p>
              </div>
              <Clock className="w-8 h-8 sm:w-12 sm:h-12 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inductions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Induction Records</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-slate-600">Loading inductions...</div>
          ) : filteredInductions.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileCheck className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium">No induction records found</p>
              <p className="text-sm mt-2">Generate a QR code for personnel to complete their induction</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Induction #</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Site Manager</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInductions.map((induction) => {
                    const isCompleted = Boolean(induction.inductor_signature);
                    return (
                      <TableRow key={induction.id}>
                        <TableCell className="font-medium">{induction.induction_number}</TableCell>
                        <TableCell>
                          {induction.first_name} {induction.surname}
                        </TableCell>
                        <TableCell>{induction.company_name}</TableCell>
                        <TableCell>{induction.contact_number}</TableCell>
                        <TableCell>
                          {induction.inductor_name || (
                            <span className="text-slate-400 italic">Not signed</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {new Date(induction.created_at).toLocaleDateString("en-AU")}
                        </TableCell>
                        <TableCell>
                          {isCompleted ? (
                            <Badge className="bg-green-600">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Completed
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300">
                              <Clock className="w-3 h-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedInduction(induction);
                                setShowViewDialog(true);
                              }}
                            >
                              <FileCheck className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            {user?.role !== "view_only" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteConfirm({
                                  id: induction.id,
                                  name: `${induction.first_name} ${induction.surname}`
                                })}
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Site Induction QR Code</DialogTitle>
            <DialogDescription>
              QR code for {selectedSiteName}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {qrCodeUrl && (
              <div className="flex flex-col items-center">
                <img src={qrCodeUrl} alt="Induction QR Code" className="w-full max-w-sm" />
                <p className="text-sm text-slate-600 mt-4 text-center">
                  Personnel scan this code to complete their site induction form
                </p>
              </div>
            )}

            <div className="flex space-x-2">
              <Button onClick={downloadQRCode} className="flex-1">
                <Download className="w-4 h-4 mr-2" />
                Download QR Code
              </Button>
              <Button onClick={() => setShowQRDialog(false)} variant="outline" className="flex-1">
                Close
              </Button>
            </div>

            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <p className="text-xs text-green-800">
                <strong>✅ Permanent QR Code:</strong> This QR code is unique to this site and will never change.
                Display it at the site entrance.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Induction Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedInduction && (
            <>
              {/* Header with Logo and Site Details */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 -m-6 mb-6 rounded-t-lg print:bg-blue-600">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-4">
                    <img src="/logo.png?v=3" alt="DCS Built" className="w-16 h-16 bg-white rounded p-1" />
                    <div>
                      <h2 className="text-2xl font-bold">Site Induction Record</h2>
                      <p className="text-blue-100 mt-1">
                        {sites.find(s => s.id === selectedInduction.site_id)?.name || "Site"}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm opacity-90">Induction Number</p>
                    <p className="text-xl font-bold">{selectedInduction.induction_number}</p>
                    <p className="text-sm opacity-90 mt-2">
                      Date: {new Date(selectedInduction.created_at).toLocaleDateString("en-AU")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Print Button */}
              <div className="flex justify-end mb-4 print:hidden">
                <Button onClick={handlePrintInduction} variant="outline">
                  <Printer className="w-4 h-4 mr-2" />
                  Print Induction
                </Button>
              </div>

              <div className="space-y-6">
              {/* Personal Information */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">Personal Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Name:</span> {selectedInduction.first_name} {selectedInduction.surname}
                  </div>
                  <div>
                    <span className="font-medium">Company:</span> {selectedInduction.company_name}
                  </div>
                  <div>
                    <span className="font-medium">Occupation:</span> {selectedInduction.occupation}
                  </div>
                  <div>
                    <span className="font-medium">Employment:</span> {selectedInduction.employment_status}
                  </div>
                  <div>
                    <span className="font-medium">DOB:</span> {selectedInduction.date_of_birth}
                  </div>
                  <div>
                    <span className="font-medium">Contact:</span> {selectedInduction.contact_number}
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">Emergency Contact</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Name:</span> {selectedInduction.emergency_contact_name}
                  </div>
                  <div>
                    <span className="font-medium">Relationship:</span> {selectedInduction.emergency_relationship}
                  </div>
                  <div>
                    <span className="font-medium">Phone:</span> {selectedInduction.emergency_phone}
                  </div>
                  {selectedInduction.emergency_mobile && (
                    <div>
                      <span className="font-medium">Mobile:</span> {selectedInduction.emergency_mobile}
                    </div>
                  )}
                </div>
              </div>

              {/* Medical Information */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">Medical Information</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium">Pre-existing Injury:</span>{" "}
                    {selectedInduction.pre_existing_injury ? (
                      <span className="text-orange-600">
                        Yes - {selectedInduction.pre_existing_injury_details}
                      </span>
                    ) : (
                      "No"
                    )}
                  </div>
                  <div>
                    <span className="font-medium">Medication/Allergy:</span>{" "}
                    {selectedInduction.medication_allergy ? (
                      <span className="text-orange-600">
                        Yes - {selectedInduction.medication_allergy_details}
                      </span>
                    ) : (
                      "No"
                    )}
                  </div>
                </div>
              </div>

              {/* Licenses & Competencies */}
              {(selectedInduction.identification_type || 
                selectedInduction.industry_induction_card_number || 
                selectedInduction.high_risk_license_type ||
                selectedInduction.other_competencies) && (
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold mb-3">Licenses & Competencies</h3>
                  <div className="space-y-2 text-sm">
                    {selectedInduction.identification_type && (
                      <div>
                        <span className="font-medium">ID Type:</span> {selectedInduction.identification_type}
                      </div>
                    )}
                    {selectedInduction.industry_induction_card_number && (
                      <div>
                        <span className="font-medium">Industry Induction Card:</span> {selectedInduction.industry_induction_card_number}
                      </div>
                    )}
                    {selectedInduction.high_risk_license_type && (
                      <div>
                        <span className="font-medium">High Risk License:</span> {selectedInduction.high_risk_license_type}
                        {selectedInduction.high_risk_card_number && ` (${selectedInduction.high_risk_card_number})`}
                        {selectedInduction.high_risk_classes && ` - Classes: ${selectedInduction.high_risk_classes}`}
                        {selectedInduction.high_risk_expiry && ` - Expires: ${selectedInduction.high_risk_expiry}`}
                      </div>
                    )}
                    {selectedInduction.other_competencies && (
                      <div>
                        <span className="font-medium">Other Competencies:</span> {selectedInduction.other_competencies}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Declarations */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">Declarations</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center">
                    {selectedInduction.induction_explained ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600 mr-2" />
                    ) : (
                      <Clock className="w-4 h-4 text-gray-400 mr-2" />
                    )}
                    Induction explained by employer
                  </div>
                  <div className="flex items-center">
                    {selectedInduction.understand_induction ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600 mr-2" />
                    ) : (
                      <Clock className="w-4 h-4 text-gray-400 mr-2" />
                    )}
                    Understand induction requirements
                  </div>
                  <div className="flex items-center">
                    {selectedInduction.agree_to_comply ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600 mr-2" />
                    ) : (
                      <Clock className="w-4 h-4 text-gray-400 mr-2" />
                    )}
                    Agree to comply with site rules
                  </div>
                  <div className="flex items-center">
                    {selectedInduction.opportunity_for_questions ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600 mr-2" />
                    ) : (
                      <Clock className="w-4 h-4 text-gray-400 mr-2" />
                    )}
                    Opportunity for questions provided
                  </div>
                  <div className="flex items-center">
                    {selectedInduction.employer_communicated_swms ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600 mr-2" />
                    ) : (
                      <Clock className="w-4 h-4 text-gray-400 mr-2" />
                    )}
                    Employer communicated SWMS
                  </div>
                </div>
              </div>

              {/* Signatures */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Signatures</h3>
                <div className="grid grid-cols-2 gap-6">
                  {/* Inductee Signature */}
                  <div>
                    <p className="text-sm font-medium mb-2">Inductee Signature</p>
                    {selectedInduction.inductee_signature ? (
                      <div className="border rounded p-2 bg-white">
                        <img 
                          src={selectedInduction.inductee_signature} 
                          alt="Inductee Signature" 
                          className="max-h-32 mx-auto"
                        />
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">No signature</p>
                    )}
                    <p className="text-xs text-gray-600 mt-1">
                      Date: {selectedInduction.inductee_signature_date}
                    </p>
                  </div>

                  {/* Site Manager Signature */}
                  <div>
                    <p className="text-sm font-medium mb-2">Site Manager Countersignature</p>
                    {selectedInduction.inductor_signature ? (
                      <div>
                        <div className="border rounded p-2 bg-white">
                          <img 
                            src={selectedInduction.inductor_signature} 
                            alt="Manager Signature" 
                            className="max-h-32 mx-auto"
                          />
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          Name: {selectedInduction.inductor_name}
                        </p>
                        <p className="text-xs text-gray-600">
                          Date: {selectedInduction.inductor_signature_date}
                        </p>
                      </div>
                    ) : (
                      <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300">
                        <Clock className="w-3 h-3 mr-1" />
                        Pending Manager Signature
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer Info */}
              <div className="bg-slate-50 p-3 rounded text-xs text-slate-600">
                <p><strong>Created:</strong> {new Date(selectedInduction.created_at).toLocaleString("en-AU")}</p>
              </div>

              <div className="flex justify-end">
                <Button onClick={() => setShowViewDialog(false)}>
                  Close
                </Button>
              </div>
            </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Induction Dialog */}
      <Dialog open={showAddInductionDialog} onOpenChange={setShowAddInductionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Site Induction</DialogTitle>
            <DialogDescription>
              Select a site to complete an induction form
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="add-induction-site">Site *</Label>
              <Select
                value={addInductionSite}
                onValueChange={setAddInductionSite}
              >
                <SelectTrigger id="add-induction-site">
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex space-x-2 pt-4">
              <Button 
                className="flex-1"
                onClick={() => {
                  if (addInductionSite) {
                    window.open(`/induction/${addInductionSite}`, '_blank');
                    setShowAddInductionDialog(false);
                    setAddInductionSite("");
                  } else {
                    toast.error("Please select a site");
                  }
                }}
              >
                Open Induction Form
              </Button>
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setShowAddInductionDialog(false);
                  setAddInductionSite("");
                }}
              >
                Cancel
              </Button>
            </div>

            <p className="text-xs text-slate-500 italic">
              The induction form will open in a new tab for completion and manager countersignature.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm !== null} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the induction record for {deleteConfirm?.name}?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-red-600 font-medium">
              ⚠️ This action cannot be undone. The induction record will be permanently deleted.
            </p>
            <div className="flex space-x-2">
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => handleDeleteInduction(deleteConfirm.id, deleteConfirm.name)}
              >
                Delete
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
